'use client';
import React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
   Box,
   Button,
   Flex,
   Table,
   Tbody,
   Td,
   Text,
   Tr,
   useColorModeValue,
   Input,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalBody,
   ModalFooter,
   IconButton,
   useDisclosure,
   Select,
   useOutsideClick,
   useBreakpointValue,
   Switch,
   Badge,
   useColorMode,
} from '@chakra-ui/react';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import 'jspdf-autotable';
import Card from 'components/card/Card';
import { GridItem } from '@chakra-ui/react';
import DeleteDialog from '../masters/components/DeleteDialog';
import nodata from 'assets/img/nfts/no data.svg';
import { Thead, Th, Grid } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { columnsglobalIMEI } from '../customDataTable/variables/columnsData';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';
import { getAllGlboalIMEI } from '../../../redux/actions/GlobalIMEI';
import { addGlboalIMEI, deleteGlboalIMEI, updateGlboalIMEI } from '../../../redux/actions/GlobalIMEI';
import { useDispatch } from 'react-redux';
import { getUser } from '../../../redux/actions/user';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import DynamicTable from 'views/admin/masters/components/DynamicAllDataTable';
import { SearchIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { useUser } from 'contexts';

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

const GlobalIMEI = () => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const [searchQuery, setSearchQuery] = React.useState('');
   const [selectedRowId, setSelectedRowId] = React.useState(null);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
   const columnHelper = createColumnHelper();
   const GlobalIMEI = useSelector((state) => state.globalImei?.list || []);
   const pagination = useSelector((state) => state.globalImei?.pagination || []);
   const [data, setData] = React.useState([...GlobalIMEI]);
   const profiler = useSelector((state) => state.user?.profile || []);
   const [currentRow, setCurrentRow] = React.useState({});
   const [isEditMode, setIsEditMode] = React.useState(false);
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const [isOpen, setIsOpen] = useState(false);
   const [error, setError] = useState('');
   const [imei, setImei] = useState('');
   const [reason, setReason] = useState('');
   const [sorting, setSorting] = React.useState([]);
   const [currentPage, setCurrentPage] = useState(1);
   const [limit, setLimit] = useState(10);
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');
   const backgroundColor = useColorModeValue('white', 'backgroundColor');
   const { onOpen, onClose } = useDisclosure();
   const dispatch = useDispatch();
   const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });
   const { user } = useUser();
   const Admin = user?.isAdmin;
   const companyId = Number(localStorage.getItem('companyId'));
   const globalImeiRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.globalImei
      : null;

   useEffect(() => {
      dispatch(getUser());
   }, [dispatch]);

   useEffect(() => {
      setData([...GlobalIMEI]);
   }, [GlobalIMEI]);

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = React.useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 500),
      [],
   );

   const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchQuery(value);
      debouncedSearchHandler(value);
   };

   useEffect(() => {
      dispatch(
         getAllGlboalIMEI({
            page: currentPage,
            search: debouncedSearchQuery,
            sortBy: sorting.length ? sorting[0]?.id : 'id',
            sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
         }),
      );
   }, [dispatch, currentPage, limit, debouncedSearchQuery, sorting]);

   const handlePageChange = (direction) => {
      const newPage = direction === 'next' ? pagination.nextPage : pagination.previousPage;
      if (newPage) setCurrentPage(newPage);
   };

   const [columnsData, setColumnsData] = React.useState([
      {
         id: 'imeiNo',
         header: 'IMEI No',
         accessor: 'imeiNo',
         index: 0,
      },
      {
         id: 'reason',
         header: 'Reason',
         accessor: 'reason',
         index: 1,
      },
      {
         id: 'globalImeiStatus',
         header: 'Status',
         accessor: 'globalImeiStatus',
         index: 2,
         cell: (info) => {
            const status = info.getValue();
            let statusText, colorScheme;
            switch (status) {
               case 0:
                  statusText = 'Pending';
                  colorScheme = 'yellow';
                  break;
               case 1:
                  statusText = 'Approved';
                  colorScheme = 'green';
                  break;
               case 2:
                  statusText = 'Rejected';
                  colorScheme = 'red';
                  break;
               default:
                  statusText = 'Unknown';
                  colorScheme = 'gray';
            }
            return (
               <Badge colorScheme={colorScheme} variant="solid" borderRadius="full" px={3} py={1}>
                  {statusText}
               </Badge>
            );
         },
      },
   ]);

   const handlePageSizeChange = (e) => {
      const value = e.target.value;
      const newLimit = value === 'ALL' ? pagination?.totalProducts : Number(value);
      setLimit(newLimit);
      setCurrentPage(1);
   };

   useEffect(() => {
      if (isEditMode && currentRow) {
         setImei(currentRow.imeiNo);
         setReason(currentRow.reason);
      }
   }, [isEditMode, currentRow]);

   const filteredData = React.useMemo(() => {
      if (!searchQuery) return GlobalIMEI;
      return GlobalIMEI?.filter((row) =>
         columnsData?.some((col) => String(row[col.id]).toLowerCase().includes(searchQuery.toLowerCase())),
      );
   }, [GlobalIMEI, searchQuery, columnsData]);

   const [columnVisibility, setColumnVisibility] = React.useState(
      columnsData.reduce((acc, column) => {
         acc[column.id] = true;
         return acc;
      }, {}),
   );

   const columns = columnsData
      .filter((column) => columnVisibility[column.id])
      .map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => (
               <Text justifyContent="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }}>
                  {column.header}
               </Text>
            ),
            cell: column.cell || ((info) => <Flex align="center">{info.getValue()}</Flex>),
         }),
      );

   const table = useReactTable({
      data: filteredData,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      state: {
         sorting,
      },
      onSortingChange: setSorting,
   });

   const openModal = () => {
      setIsOpen(true);
   };

   const closeModal = () => {
      if (!isEditMode) {
         setImei('');
         setReason('');
      }
      setIsOpen(false);
   };

   const handleSave = () => {
      if (imei.length !== 15) {
         setError('Please enter a valid IMEI number.');
         return;
      }
      const imeidata = { imeiNo: imei, reason: reason };

      if (isEditMode && currentRow.id) {
         const imeidata = { id: currentRow.id, imeiNo: imei, reason: reason };
         dispatch(updateGlboalIMEI(imeidata));
      } else {
         dispatch(addGlboalIMEI(imeidata));
      }
      dispatch(getAllGlboalIMEI());
      closeModal();
      setImei('');
      setReason('');
   };

   const handleCansel = () => {
      closeModal();
      setImei('');
      setReason('');
   };

   const textColor = useColorModeValue('secondaryGray.900', 'white');

   columns.push(
      columnHelper.accessor('id', {
         id: 'id',
         header: () => null,
         cell: (info) => (
            <Flex justify="end">
               {info.row.original.createdBy === profiler.id && (
                  <Box className="edit-icon-container">
                     <IconButton
                        aria-label="Edit"
                        icon={<EditIcon />}
                        variant="ghost"
                        colorScheme="brand"
                        onClick={(e) => {
                           e.stopPropagation();
                           setCurrentRow(info.row.original);
                           setIsEditMode(true);
                           openModal(true);
                        }}
                     />
                     <IconButton
                        aria-label="Delete"
                        icon={<DeleteIcon />}
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => {
                           e.stopPropagation();
                           handleDeleteClick(info.row.original.id);
                        }}
                     />
                  </Box>
               )}
            </Flex>
         ),
      }),
   );

   const handleDelete = async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteGlboalIMEI(selectedRowId));
            setData(data?.filter((row) => row.id !== selectedRowId));
            setDeleteDialogOpen(false);
            dispatch(getAllGlboalIMEI());
         } catch (err) {
            console.error('Failed to delete:', err);
         }
      }
   };

   const handleDeleteClick = (id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   };

   const [expanded, setExpanded] = useState(false);
   const wrapperRef = useRef();
   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   useEffect(() => {
      const handleEscKey = (event) => {
         if (event.key === 'Escape' && isOpen) {
            closeModal();
         }
      };

      if (isOpen) {
         document.addEventListener('keydown', handleEscKey);
         // Prevent background scroll when modal is open
         document.body.style.overflow = 'hidden';
      }

      return () => {
         document.removeEventListener('keydown', handleEscKey);
         document.body.style.overflow = 'unset';
      };
   }, [isOpen, closeModal]);

   return (
      <Grid overflow="auto">
         <Flex className="px-6 mb-4 justify-between items-center">
            <Flex>
               {/* Enhanced Modal */}
               <Modal isOpen={isOpen} onClose={closeModal} isCentered size={'xl'}>
                  <div
                     className={`fixed inset-0  z-[999] transition-opacity duration-300 ${
                        isOpen ? 'bg-black bg-opacity-50' : 'bg-opacity-0'
                     } flex items-center justify-center p-4 z-50`}>
                     <div className="fixed inset-0" onClick={closeModal}></div>

                     <div
                        className={`relative w-full max-w-2xl transform transition-all duration-300 ${
                           isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                        } rounded-xl shadow-2xl ${
                           isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                        {/* Modal Header */}
                        <div className={`px-6 py-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                           <div className="flex items-center justify-between">
                              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                 {isEditMode ? 'Edit' : 'Add'} IMEI Number
                              </h2>
                              <button
                                 onClick={closeModal}
                                 className={`p-2 rounded-full transition-colors ${
                                    isDark
                                       ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                                       : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                                 }`}>
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth={2}
                                       d="M6 18L18 6M6 6l12 12"
                                    />
                                 </svg>
                              </button>
                           </div>
                           <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              {isEditMode ? 'Update the existing IMEI details' : 'Add a new IMEI number to the system'}
                           </p>
                        </div>

                        {/* Modal Body */}
                        <div className="px-6 py-6">
                           <div className="space-y-6">
                              {/* IMEI Input Field */}
                              <div>
                                 <label
                                    className={`block text-sm font-semibold mb-3 ${
                                       isDark ? 'text-gray-200' : 'text-gray-700'
                                    }`}>
                                    Enter IMEI Number
                                    <span className="text-red-500 ml-1">*</span>
                                 </label>
                                 <div className="relative">
                                    <input
                                       type="text"
                                       placeholder="Enter 15-digit IMEI number"
                                       value={imei}
                                       onChange={(e) => setImei(e.target.value)}
                                       className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 ${
                                          isDark
                                             ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20'
                                             : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20'
                                       }`}
                                       maxLength={15}
                                    />
                                    <div
                                       className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs ${
                                          isDark ? 'text-gray-400' : 'text-gray-500'
                                       }`}>
                                       {imei.length}/15
                                    </div>
                                 </div>
                              </div>

                              {/* Reason Input Field */}
                              <div>
                                 <label
                                    className={`block text-sm font-semibold mb-3 ${
                                       isDark ? 'text-gray-200' : 'text-gray-700'
                                    }`}>
                                    Reason
                                    <span className="text-red-500 ml-1">*</span>
                                 </label>
                                 <textarea
                                    placeholder="Enter reason for adding this IMEI..."
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 resize-none ${
                                       isDark
                                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/20'
                                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500/20'
                                    }`}
                                 />
                              </div>

                              {/* Error Message */}
                              {error && (
                                 <div
                                    className={`p-4 rounded-lg border ${
                                       isDark
                                          ? 'bg-red-900/20 border-red-800 text-red-200'
                                          : 'bg-red-50 border-red-200 text-red-700'
                                    }`}>
                                    <div className="flex items-center">
                                       <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                          <path
                                             fillRule="evenodd"
                                             d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                             clipRule="evenodd"
                                          />
                                       </svg>
                                       <span className="font-medium">Error: {error}</span>
                                    </div>
                                 </div>
                              )}
                           </div>
                        </div>

                        {/* Modal Footer */}
                        <div
                           className={`px-6 py-4 border-t ${
                              isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                           } rounded-b-xl`}>
                           <div className="flex justify-end space-x-3">
                              <button
                                 onClick={handleCansel}
                                 className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 border ${
                                    isDark
                                       ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white'
                                       : 'border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                                 }`}>
                                 Cancel
                              </button>
                              <button
                                 onClick={handleSave}
                                 disabled={!imei.trim() || !reason.trim()}
                                 className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                                    !imei.trim() || !reason.trim()
                                       ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                                       : isDark
                                       ? 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-500/25'
                                       : 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/25'
                                 } transform hover:scale-105 disabled:transform-none disabled:hover:scale-100`}>
                                 <div className="flex items-center">
                                    {isEditMode ? (
                                       <>
                                          <svg
                                             className="w-4 h-4 mr-2"
                                             fill="none"
                                             stroke="currentColor"
                                             viewBox="0 0 24 24">
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                             />
                                          </svg>
                                          Update IMEI
                                       </>
                                    ) : (
                                       <>
                                          <svg
                                             className="w-4 h-4 mr-2"
                                             fill="none"
                                             stroke="currentColor"
                                             viewBox="0 0 24 24">
                                             <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                             />
                                          </svg>
                                          Save IMEI
                                       </>
                                    )}
                                 </div>
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </Modal>
            </Flex>
         </Flex>
         <Box className="mt-14" bg={backgroundColor}>
            <Flex px="25px" mb="5px" justifyContent="space-between" align="center" overflowX="auto">
               <Flex align="center" gap={10}>
                  <Text color={textColor} fontSize={{ base: '17px', lg: '22px' }} fontWeight="700" lineHeight="100%">
                     Global IMEI
                  </Text>
               </Flex>
               <Flex align="center" gap={2} flexShrink={0} p={3}>
                  {!isMobileOrTablet && (Admin || globalImeiRight?.add) && (
                     <button
                        onClick={() => {
                           setCurrentRow({});
                           setIsEditMode(false);
                           onOpen();
                           openModal(true);
                        }}
                        className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${
                           isDark
                              ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                              : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                        }`}>
                        {/* Gradient overlay on hover */}
                        <span
                           className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${
                              isDark ? 'via-cyan-100/20' : 'via-white/30'
                           }`}></span>

                        {/* Icon container with background */}
                        <span
                           className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                              isDark
                                 ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30'
                                 : 'bg-white/20 group-hover:bg-white/30'
                           }`}>
                           <svg
                              className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                           </svg>
                        </span>

                        <span className="relative text-base font-semibold">Add New</span>
                     </button>
                  )}
                  <MotionFlex
                     ref={wrapperRef}
                     align="center"
                     bg={bg}
                     borderRadius="full"
                     me="20px"
                     cursor="pointer"
                     px={expanded ? 3 : 0}
                     w={expanded ? '200px' : '40px'}
                     h="40px"
                     transition="all 0.3s ease"
                     onClick={() => setExpanded(true)}>
                     <MotionSearchIcon
                        color={icon}
                        animate={{ rotate: expanded ? 0 : 90 }}
                        transition={{ duration: 0.3 }}
                        style={{ marginRight: expanded ? '8px' : '10px', marginLeft: expanded ? '0' : '10px' }}
                     />
                     {expanded && (
                        <Input
                           variant="unstyled"
                           placeholder="Search..."
                           value={searchQuery}
                           color={icon}
                           onChange={handleSearchChange}
                           autoFocus
                        />
                     )}
                  </MotionFlex>

                  {isMobileOrTablet && (Admin || globalImeiRight?.add) && (
                     <Box position="fixed" bottom="4" right="4" zIndex={999}>
                        <button
                           onClick={() => {
                              setCurrentRow({});
                              setIsEditMode(false);
                              onOpen();
                              openModal(true);
                           }}
                           className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${
                              isDark
                                 ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                                 : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                           }`}>
                           {/* Gradient overlay on hover */}
                           <span
                              className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${
                                 isDark ? 'via-cyan-100/20' : 'via-white/30'
                              }`}></span>

                           {/* Icon container with background */}
                           <span
                              className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${
                                 isDark
                                    ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30'
                                    : 'bg-white/20 group-hover:bg-white/30'
                              }`}>
                              <svg
                                 className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                                 fill="none"
                                 stroke="currentColor"
                                 viewBox="0 0 24 24">
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d="M12 4v16m8-8H4"
                                 />
                              </svg>
                           </span>

                           <span className="relative text-base font-semibold">Add New</span>
                        </button>
                     </Box>
                  )}
               </Flex>
            </Flex>
            <DynamicTable
               table={table}
               data={data}
               emptyMessage="No IMEI data found"
               onEditClick={(row) => {
                  setCurrentRow(row);
                  setIsEditMode(true);
                  onOpen();
                  openModal(true);
               }}
               onDeleteClick={(id) => {
                  handleDeleteClick(id);
               }}
               canDelete={true}
               right={globalImeiRight}
               columnVisibility={columnVisibility}
               columnsData={columnsData}
               sorting={sorting}
               limit={limit}
               setCurrentRow={setCurrentRow}
               onOpen={openModal}
               setIsEditMode={setIsEditMode}
               setDeleteDialogOpen={setDeleteDialogOpen}
               setSelectedRowId={setSelectedRowId}
               debouncedSearchQuery={debouncedSearchQuery}
            />
         </Box>

         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="IMEI"
         />
      </Grid>
   );
};

export default GlobalIMEI;
